import { useState } from 'react';
import { AutomobileMaintenanceSchedules } from '@/reports/types/vehicles';
import {
  Card,
  Select,
  SelectOption,
  Stack,
  SwitchLabel,
  Text,
} from '@/theme/index';
import { RoutineMaintenanceSchedule, RoutineTableContent } from './components';

interface RoutineMaintenanceProps {
  vehicleMaintenanceSchedules: AutomobileMaintenanceSchedules;
  isSevereDriving: boolean;
  setIsSevereDriving: (value: boolean) => void;
  hasSevereDrivingItems?: boolean;
}

export function RoutineMaintenance({
  vehicleMaintenanceSchedules,
  isSevereDriving,
  setIsSevereDriving,
  hasSevereDrivingItems = true,
}: RoutineMaintenanceProps) {
  const [currentlySelectedCategory, setCurrentlySelectedCategory] =
    useState<string>('');

  function handleSwitchLabelChange(value: boolean) {
    setIsSevereDriving(value);
    setCurrentlySelectedCategory('');
  }

  const categories = generateCategories(vehicleMaintenanceSchedules);
  const routineMaintenance = generateRoutineMaintenanceData(
    vehicleMaintenanceSchedules,
    currentlySelectedCategory
  );

  return (
    <Card variant="outlined">
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        width="100%"
        justifyContent="space-between"
        mb={2}
      >
        <Text variant="h2">Routine Maintenance</Text>
        {hasSevereDrivingItems && (
          <SwitchLabel
            label="Severe Driving"
            tooltip="Demanding or harsh driving conditions that can accelerate wear and tear on your vehicle."
            checked={isSevereDriving}
            setChecked={handleSwitchLabelChange}
            tooltipStart
          />
        )}
      </Stack>
      <Text variant="body2">
        Regular maintenance is key to ensuring your vehicle's optimal
        performance and longevity. Our interval-based service schedule helps you
        keep track of essential maintenance tasks that should be performed based
        on mileage or time intervals, whichever comes first. By adhering to this
        schedule, you can prevent potential issues, improve fuel efficiency, and
        extend the life of your vehicle.
      </Text>
      <Text variant="h4" mb={1} mt={2}>
        Categories
      </Text>
      <Select
        options={categories}
        onOptionSelect={setCurrentlySelectedCategory}
        value={currentlySelectedCategory}
        defaultValue=""
        id="routine-maintenance-categories-filter"
        data-cy="routine_maintenance_categories_filter"
      />
      {RoutineTableContent(routineMaintenance)}
    </Card>
  );
}

function generateCategories(
  vehicleMaintenanceSchedules: AutomobileMaintenanceSchedules
) {
  const output: SelectOption[] = [{ value: '', display: 'All' }];

  const categories = vehicleMaintenanceSchedules.reduce(
    (output: string[], maintenance) => {
      const category = maintenance.category || '';
      if (!output.includes(category || '')) {
        output.push(category || '');
      }
      return output;
    },
    []
  );

  categories.sort();

  categories.forEach((category) => {
    output.push({ value: category, display: category });
  });

  return output;
}

function generateRoutineMaintenanceData(
  schedules: AutomobileMaintenanceSchedules,
  category: string
) {
  if (category) {
    schedules = schedules.filter((schedule) => {
      return schedule.category === category;
    });
  }

  const maintenance = schedules.reduce(
    (output: RoutineMaintenanceSchedule[], schedule) => {
      const time = schedule.intervals?.find((interval) => {
        return (
          interval.type?.toUpperCase() === 'EVERY' &&
          interval.units?.toUpperCase() === 'MONTHS'
        );
      });
      const distance = schedule.intervals?.find((interval) => {
        return (
          interval.type?.toUpperCase() === 'EVERY' &&
          interval.units?.toUpperCase() === 'MILES'
        );
      });

      if (time || distance) {
        output.push({
          category: schedule.category || '',
          item: schedule.name || '',
          mileage: distance?.value || 0,
          months: time?.value || 0,
        });
      }

      return output;
    },
    []
  );

  maintenance.sort((a, b) => {
    const categoryComparison = a.category.localeCompare(b.category);
    const mileageComparison = a.mileage - b.mileage;

    return categoryComparison === 0 ? mileageComparison : categoryComparison;
  });

  return maintenance;
}
